import useDiscoveryFetch from './useDiscoveryFetch';
import MobileCompaniesSortDrawer from './MobileCompaniesSortDrawer';
import MobileCompanyResults from './MobileCompanyResults';
import { mobileCompanySortDefinitions, mobileCompanySortOptions, getMobileSortedCompanies } from './mobileCompanySorting';
import { useState, useEffect, useMemo, useRef } from 'react';
import { Table, Form, Row, Col, Card } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Pagination from 'react-bootstrap/Pagination';
import { useMediaQuery } from 'react-responsive';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import api from '../../api/axiosInstance';
import { trackEvent } from '../../services/analytics/analyticsService';
import { ANALYTICS_EVENTS } from '../../services/analytics/analyticsEvents';
import defaultLogo from '../../assets/img/ic-img.svg';
import defaultCompanyMarkerLogo from '../../assets/img/ic-building-company.svg';
import Sidebar from '../../Sidebar';
import Breadcrumb from '../../Breadcrumb';
import DiscoveryMap from './DiscoveryMap';
import DiscoveryFilterBar from './DiscoveryFilterBar';
import MobileDiscoveryHeading from './MobileDiscoveryHeading';
import MobileDiscoverySearchToolbar from './MobileDiscoverySearchToolbar';
import MobileDiscoveryViewSwitch from './MobileDiscoveryViewSwitch';
import DiscoveryDesktopAiSlot from './DiscoveryDesktopAiSlot';
import NavTabs from '../../NavTabs';
import useMobileDiscoveryNavDocking from './useMobileDiscoveryNavDocking';
import useMobileLoadMore from './useMobileLoadMore';
import MobileAppliedChips from './MobileAppliedChips';
import { captureDiscoveryFilters, buildDiscoveryFilterChipLabels } from './discoveryMobileFilters';
import icFilter from '../../assets/img/ic-filter.svg';
import icSort from '../../assets/img/ic-sort.svg';
import icStar from '../../assets/img/ic-star.svg';
import icLink from '../../assets/img/ic-link-square.svg';

import icHeartEmpty from '../../assets/img/ic-heart-empty.svg';
import icHeartFull from '../../assets/img/ic-heart-full.svg';
import countriesData from '../../data/countries';
import { useMobileAiPageContext } from '../../MobileAiAssistantContext';
import { getDiscoveryAiSuggestions } from '../../utils/discoveryAiSuggestions';

const PAGE_DISCOVERY_TYPE = 'companies';


// CONTRACT: this mapping must stay identical to AI_INDUSTRY_GROUPS in the backend's
// discoveryAiContextService.js. The backend uses it to decide which companies are in
// the AI's matchedCompanies (what the AI's answer states); this page uses the identical
// mapping to reproduce that same result set when a filter chip navigates here with
// ?industry=<label>. If the two mappings ever diverge, the AI's answer and this page's
// result set will disagree for group-based queries. Update both together.
//
// "Financial Services" alone resolves too narrowly to NACE 66 (auxiliary activities)
// and misses banking (64) and insurance (65), so it's matched against all three.
// "Software" covers NACE 62 (programming/consultancy) and 63 (information services).
const AI_INDUSTRY_GROUPS = {
    'financial services': ['64', '65', '66'],
    'software': ['62', '63'],
};

const DiscoveryCompanyList = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    // Keep the last explicitly applied comparator when fresh API data arrives.
    const refreshSortRef = useRef(null);
    const [companies, setCompanies] = useState([]);
    const [favorites, setFavorites] = useState(new Set());
    const [sortOrderName, setSortOrderName] = useState('asc');
    const [sortOrderCountry, setSortOrderCountry] = useState('asc');
    const [sortOrderCity, setSortOrderCity] = useState('asc');
    const [sortOrderIndustry, setSortOrderIndustry] = useState('asc');
    const [sortOrderGlobalSize, setSortOrderGlobalSize] = useState('asc');
    const [sortOrderRating, setSortOrderRating] = useState('asc');
    const [sortOrderLastUpdate, setSortOrderLastUpdate] = useState('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const [companiesPerPage, setCompaniesPerPage] = useState(10);
    const [selectedIndustry, setSelectedIndustry] = useState('');
    const [industriesData, setIndustriesData] = useState([]);

    // Initialized once from the URL; ?industry=Financial Services stays a group match
    // for the life of the page rather than a single selectedIndustry code. Real state
    // (not derived from searchParams on every render) so Clear All can actually clear it.
    const [aiIndustryGroup, setAiIndustryGroup] = useState(() => {
        const rawParam = String(searchParams.get('industry') || '').trim();
        return AI_INDUSTRY_GROUPS[rawParam.toLowerCase()] ? rawParam : null;
    });

    const clearAiIndustryGroup = () => {
        setAiIndustryGroup(null);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('industry');
            return next;
        }, { replace: true });
    };

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCountry, setSelectedCountry] = useState(searchParams.get('country') || '');
    const [selectedCity, setSelectedCity] = useState(searchParams.get('city') || '');
    const [selectedStudyDirection, setSelectedStudyDirection] = useState('');
    const [selectedCompanySize, setSelectedCompanySize] = useState('');
    const [selectedCompanyRating, setSelectedCompanyRating] = useState('');
    const [selectedEnglishSpeaking, setSelectedEnglishSpeaking] = useState(
        searchParams.get('englishSpeaking') === 'true' ? true : null
    );
    const [selectedWorldTraveler, setSelectedWorldTraveler] = useState(null);
    const [selectedStudentJobAreas, setSelectedStudentJobAreas] = useState(
        searchParams.get('studentFriendly') === 'true' ? true : null
    );
    const [selectedInternship, setSelectedInternship] = useState(
        searchParams.get('internship') === 'true' ? true : null
    );
    const [selectedFavorites, setSelectedFavorites] = useState(null);
    const [savedFilterSets, setSavedFilterSets] = useState([]);
    const [selectedFilterSet, setSelectedFilterSet] = useState('default');
    const [activeCityNames, setActiveCityNames] = useState(new Set());
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [showMobileSort, setShowMobileSort] = useState(false);
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);
    const [draftSortField, setDraftSortField] = useState(null);
    const [draftSortDirection, setDraftSortDirection] = useState(null);
    const [appliedMobileFilters, setAppliedMobileFilters] = useState(() => captureDiscoveryFilters({
        searchQuery,
        selectedCountry,
        selectedCity,
        selectedIndustry,
        aiIndustryGroup,
        selectedStudyDirection,
        selectedCompanySize,
        selectedCompanyRating,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        selectedInternship,
        selectedFavorites,
    }));
    const { isMobileNavDocked, mobileMapRowRef } = useMobileDiscoveryNavDocking();

    // Matches the mobile card/filter-modal breakpoint used across Discovery pages.
    const isMobile = useMediaQuery({ query: '(max-width: 767.98px)' });

    const previousSearchRef = useRef('');
    const searchTimeoutRef = useRef(null);

    // Stable reference — prevents DiscoveryMap from resetting viewport on every render
    const mapInitialCenter = useMemo(() => ({ latitude: 50, longitude: 10, zoom: 3.4 }), []);

    const [mapFlyTo, setMapFlyTo] = useState(null);
    const companiesLoaded = companies.length > 0;
    useEffect(() => {
        if (!selectedCity || !companiesLoaded) {
            if (!selectedCity) setMapFlyTo(null);
            return;
        }
        const cityCompanies = companies.filter((c) => {
            const lat = Number(c.latitude);
            const lng = Number(c.longitude);
            return c.cityRegion === selectedCity && !Number.isNaN(lat) && Number.isFinite(lat) && !Number.isNaN(lng) && Number.isFinite(lng);
        });
        if (cityCompanies.length === 0) return;
        const avgLat = cityCompanies.reduce((s, c) => s + Number(c.latitude), 0) / cityCompanies.length;
        const avgLng = cityCompanies.reduce((s, c) => s + Number(c.longitude), 0) / cityCompanies.length;
        setMapFlyTo({ latitude: avgLat, longitude: avgLng, zoom: 10 });
    }, [selectedCity, companiesLoaded]);

    const getCompanySizeBucket = (value) => {
        const raw = String(value || '').trim().toLowerCase();

        if (!raw || raw === 'n/a' || raw === 'na' || raw === '-') return '';

        const compact = raw.replace(/employees?/g, '').replace(/\s+/g, ' ').trim();

        if (compact.includes('several 1000')) return '1,001 - 5,000 employees';
        if (compact.includes('under 30')) return '11 - 50 employees';
        if (compact.includes('over 100000')) return 'over 100000';

        const rangeMatch = compact.match(/(\d[\d,.\s]*)\s*[-–—]\s*(\d[\d,.\s]*)/);

        if (rangeMatch) {
            const min = Number(rangeMatch[1].replace(/[^\d]/g, ''));
            const max = Number(rangeMatch[2].replace(/[^\d]/g, ''));

            if (!Number.isNaN(min) && !Number.isNaN(max)) {
                if (max <= 10) return '1 - 10 employees';
                if (min >= 11 && max <= 50) return '11 - 50 employees';
                if (min >= 51 && max <= 200) return '51 - 200 employees';
                if (min >= 201 && max <= 500) return '201 - 500 employees';
                if (min >= 501 && max <= 1000) return '501 - 1,000 employees';
                if (min >= 1001 && max <= 5000) return '1,001 - 5,000 employees';
                if (min >= 5001 && max <= 10000) return '5,001 - 10,000 employees';
                if (min >= 10001 && max <= 50000) return '10001 - 50000 employees';
                if (min >= 50001 && max <= 100000) return '50001 - 100000';
                if (min > 100000) return 'over 100000';

                const avg = (min + max) / 2;

                if (avg <= 10) return '1 - 10 employees';
                if (avg <= 50) return '11 - 50 employees';
                if (avg <= 200) return '51 - 200 employees';
                if (avg <= 500) return '201 - 500 employees';
                if (avg <= 1000) return '501 - 1,000 employees';
                if (avg <= 5000) return '1,001 - 5,000 employees';
                if (avg <= 10000) return '5,001 - 10,000 employees';
                if (avg <= 50000) return '10001 - 50000 employees';
                if (avg <= 100000) return '50001 - 100000';
                return 'over 100000';
            }
        }

        const numericValue = Number(compact.replace(/[^\d]/g, ''));

        if (!Number.isNaN(numericValue) && numericValue > 0) {
            if (numericValue <= 10) return '1 - 10 employees';
            if (numericValue <= 50) return '11 - 50 employees';
            if (numericValue <= 200) return '51 - 200 employees';
            if (numericValue <= 500) return '201 - 500 employees';
            if (numericValue <= 1000) return '501 - 1,000 employees';
            if (numericValue <= 5000) return '1,001 - 5,000 employees';
            if (numericValue <= 10000) return '5,001 - 10,000 employees';
            if (numericValue <= 50000) return '10001 - 50000 employees';
            if (numericValue <= 100000) return '50001 - 100000';
            return 'over 100000';
        }

        return '';
    };

    const filteredCompanies = companies.filter((company) => {
        const normalizeStudyDirection = (val) => {
            const str = String(val).trim();

            return str
                .replace(/^\d+(?:\.\d+)?\s*[-–—]?\s*/, '')
                .replace(/([.:])\s*\d+(?:\.\d+)?\s*[-–—]?\s*/g, '$1 ')
                .replace(/^\(\s*/, '')
                .replace(/\)\s*$/, '')
                .replace(/\s{2,}/g, ' ')
                .trim()
                .toLowerCase();
        };

        const matchesSearch =
            !searchQuery ||
            company.title?.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesCountry =
            !selectedCountry || company.country === selectedCountry;

        const matchesCity =
            !selectedCity ||
            String(company.cityRegion || '').trim() === String(selectedCity || '').trim();

        const aiIndustryGroupCodes = aiIndustryGroup
            ? AI_INDUSTRY_GROUPS[aiIndustryGroup.toLowerCase()]
            : null;

        const matchesIndustry = aiIndustryGroupCodes
            ? (Array.isArray(company.industries) &&
                company.industries.some((entry) =>
                    aiIndustryGroupCodes.includes(String(entry?.industry || '').trim())
                ))
            : (
                !selectedIndustry ||
                (Array.isArray(company.industries) &&
                    company.industries.some((entry) => entry?.industry === selectedIndustry))
            );

        const matchesStudyDirection =
            !selectedStudyDirection ||
            (() => {
                const rawStudyDirections = company.studyFields?.preferredStudyDirections;

                if (Array.isArray(rawStudyDirections)) {
                    return rawStudyDirections.some(
                        (direction) =>
                            normalizeStudyDirection(direction) === normalizeStudyDirection(selectedStudyDirection)
                    );
                }

                if (typeof rawStudyDirections === 'string') {
                    return rawStudyDirections
                        .split(',')
                        .map((direction) => normalizeStudyDirection(direction))
                        .includes(normalizeStudyDirection(selectedStudyDirection));
                }

                return false;
            })();

        const companySizeValue = company.globalSize || company.localSize || '';
        const companySizeBucket = getCompanySizeBucket(companySizeValue);

        const matchesCompanySize =
            !selectedCompanySize ||
            companySizeBucket === selectedCompanySize;

        const companyRatingValue = Number(company.googleRating);
        const matchesCompanyRating =
            !selectedCompanyRating ||
            (!Number.isNaN(companyRatingValue) &&
                companyRatingValue >= Number(selectedCompanyRating));

        const matchesEnglishSpeaking =
            selectedEnglishSpeaking === null ||
            company.openedEnglishSpeaking === true;

        const matchesWorldTraveler =
            selectedWorldTraveler === null ||
            company.openedWorldTraveler === true;

        const matchesStudentJobAreas =
            selectedStudentJobAreas === null ||
            (
                Array.isArray(company.studentJobAreas)
                    ? company.studentJobAreas.length > 0
                    : Boolean(company.studentJobAreas)
            );

        const matchesInternship =
            selectedInternship === null ||
            Number(company.openJobsIntern) > 0;

        const matchesFavorites =
            selectedFavorites === null ||
            favorites.has(String(company._id));

        return (
            matchesSearch &&
            matchesCountry &&
            matchesCity &&
            matchesIndustry &&
            matchesStudyDirection &&
            matchesCompanySize &&
            matchesCompanyRating &&
            matchesEnglishSpeaking &&
            matchesWorldTraveler &&
            matchesStudentJobAreas &&
            matchesInternship &&
            matchesFavorites
        );
    });

    const previousFiltersRef = useRef(null);

    useEffect(() => {
        const trimmedSearch = searchQuery.trim();

        if (!trimmedSearch) {
            previousSearchRef.current = '';
            return;
        }

        if (previousSearchRef.current === trimmedSearch) return;

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            trackEvent(ANALYTICS_EVENTS.DISCOVERY_SEARCH_USED, {
                discoveryType: 'companies',
                searchTerm: trimmedSearch,
                resultsCount: filteredCompanies.length,
                level: 1,
                source: 'companies_search',
            });

            previousSearchRef.current = trimmedSearch;
        }, 800);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery, filteredCompanies.length]);

    useEffect(() => {
        const currentFilters = {
            selectedCountry,
            selectedCity,
            selectedIndustry,
            selectedStudyDirection,
            selectedCompanySize,
            selectedCompanyRating,
            selectedEnglishSpeaking,
            selectedWorldTraveler,
            selectedStudentJobAreas,
            selectedInternship,
            selectedFavorites,
        };

        const previousFilters = previousFiltersRef.current;

        if (!previousFilters) {
            previousFiltersRef.current = currentFilters;
            return;
        }

        Object.entries(currentFilters).forEach(([filterType, filterValue]) => {

            console.log('FILTER CHECK', {
                filterType,
                previous: previousFilters[filterType],
                current: filterValue,
            });

            if (previousFilters[filterType] !== filterValue) {

                console.log('TRACKING FILTER', {
                    filterType,
                    filterValue,
                });

                trackEvent(ANALYTICS_EVENTS.DISCOVERY_FILTER_APPLIED, {
                    discoveryType: 'companies',
                    filterType,
                    filterValue,
                    resultsCount: filteredCompanies.length,
                    level: 1,
                    source: 'companies_filter',
                });
            }
        });

        previousFiltersRef.current = currentFilters;


    }, [
        selectedCountry,
        selectedCity,
        selectedIndustry,
        selectedStudyDirection,
        selectedCompanySize,
        selectedCompanyRating,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        selectedInternship,
        selectedFavorites,
        filteredCompanies.length,
    ]);

    const indexOfLastCompany = currentPage * companiesPerPage;
    const indexOfFirstCompany = indexOfLastCompany - companiesPerPage;
    const currentCompanies = filteredCompanies.slice(indexOfFirstCompany, indexOfLastCompany);

    const mobileSortedCompanies = sortField
        ? getMobileSortedCompanies(filteredCompanies, sortField, sortDirection)
        : filteredCompanies;

    const mobileFilterKey = JSON.stringify({
        searchQuery,
        selectedCountry,
        selectedCity,
        selectedIndustry,
        aiIndustryGroup,
        selectedStudyDirection,
        selectedCompanySize,
        selectedCompanyRating,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        selectedInternship,
        selectedFavorites,
        sortOrderName,
        sortOrderCountry,
        sortOrderCity,
        sortOrderIndustry,
        sortOrderGlobalSize,
        sortOrderRating,
        sortOrderLastUpdate,
        sortField,
        sortDirection,
    });
    const mobilePagination = useMobileLoadMore({
        items: mobileSortedCompanies,
        filterKey: mobileFilterKey,
        storageKey: 'discovery-companies-mobile-list',
        isMobile,
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [
        searchQuery,
        selectedCountry,
        selectedCity,
        selectedIndustry,
        selectedStudyDirection,
        selectedCompanySize,
        selectedCompanyRating,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        selectedInternship,
        selectedFavorites,
    ]);

    const buildCurrentFiltersPayload = () => ({
        searchQuery,
        selectedCountry,
        selectedCity,
        selectedIndustry,
        selectedStudyDirection,
        selectedCompanySize,
        selectedCompanyRating,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        selectedInternship,
        selectedFavorites,
    });

    const applySavedFilterSet = (filters = {}) => {
        setSearchQuery(filters.searchQuery || '');
        setSelectedCountry(filters.selectedCountry || '');
        setSelectedCity(filters.selectedCity || '');
        setSelectedIndustry(filters.selectedIndustry || '');
        setSelectedStudyDirection(filters.selectedStudyDirection || '');
        setSelectedCompanySize(filters.selectedCompanySize || '');
        setSelectedCompanyRating(filters.selectedCompanyRating || '');
        setSelectedEnglishSpeaking(
            filters.selectedEnglishSpeaking === true ? true : null
        );
        setSelectedWorldTraveler(
            filters.selectedWorldTraveler === true ? true : null
        );
        setSelectedStudentJobAreas(
            filters.selectedStudentJobAreas === true ? true : null
        );
        setSelectedInternship(
            filters.selectedInternship === true ? true : null
        );
        setSelectedFavorites(
            filters.selectedFavorites === true ? true : null
        );
    };

    const handleSaveFilters = async (filterSetName) => {
        try {
            const trimmedName = String(filterSetName || '').trim();
            if (!trimmedName) return;

            const payload = {
                page: 'companies',
                name: trimmedName,
                filters: buildCurrentFiltersPayload(),
            };

            const res = await api.post('/api/discovery-filter-sets', payload);
            const savedFilterSet = res.data;

            setSavedFilterSets((prev) => {
                const withoutDuplicate = prev.filter(
                    (item) => item._id !== savedFilterSet._id && item.name !== savedFilterSet.name
                );

                return [savedFilterSet, ...withoutDuplicate];
            });

            setSelectedFilterSet(savedFilterSet._id);
        } catch (error) {
            console.error('Error saving discovery filter set:', error);
        }
    };

    // preserveSearch: mobile deletes a saved preset without touching the live
    // toolbar search, since Search is independent of staged/saved filter state there.
    // Desktop keeps the previous full-reset behavior.
    const handleDeleteFilterSet = async (filterSetId, { preserveSearch = false } = {}) => {
        if (!filterSetId || filterSetId === 'default') return;

        try {
            await api.delete(`/api/discovery-filter-sets/${filterSetId}`);

            const wasSelected = String(selectedFilterSet) === String(filterSetId);

            setSavedFilterSets((prev) =>
                prev.filter((filterSet) => String(filterSet._id) !== String(filterSetId))
            );

            if (wasSelected) {
                setSelectedFilterSet('default');

                if (!preserveSearch) setSearchQuery('');
                setSelectedCountry('');
                setSelectedCity('');
                setSelectedIndustry('');
                setSelectedStudyDirection('');
                setSelectedCompanySize('');
                setSelectedCompanyRating('');
                setSelectedEnglishSpeaking?.(null);
                setSelectedWorldTraveler?.(null);
                setSelectedStudentJobAreas?.(null);
                setSelectedInternship?.(null);
                setSelectedFavorites?.(null);
            }
        } catch (err) {
            console.error('Error deleting filter set:', err);
        }
    };

    const handleFilterSetChange = (value) => {
        setSelectedFilterSet(value);

        if (value === 'default') {
            applySavedFilterSet({});
            return;
        }

        const selectedSavedFilterSet = savedFilterSets.find(
            (item) => item._id === value
        );

        if (selectedSavedFilterSet?.filters) {
            applySavedFilterSet(selectedSavedFilterSet.filters);
        }
    };

    useEffect(() => {
        trackEvent(ANALYTICS_EVENTS.DISCOVERY_OPENED, {
            discoveryType: 'companies',
            viewMode: 'companies',
            level: 1,
            source: 'top_level_discovery',
        });
    }, []);

    useDiscoveryFetch(({ signal, isCurrent }) => {
        const fetchCompaniesIndustriesAndFilterSets = async () => {
            try {
                const [companiesRes, industriesRes, filterSetsRes, currentUserRes, citiesRes] = await Promise.all([
                    api.get('/api/discovery/companies', { signal }),
                    api.get('/api/nace', { signal }),
                    api.get('/api/discovery-filter-sets?page=companies', { signal }),
                    api.get('/api/users/me', { signal }),
                    api.get('/api/discovery/cities', { signal }),
                ]);
                if (!isCurrent()) return;

                const safeCompanies = Array.isArray(companiesRes.data) ? companiesRes.data : [];
                const safeIndustries = Array.isArray(industriesRes.data) ? industriesRes.data : [];
                const safeFilterSets = Array.isArray(filterSetsRes.data) ? filterSetsRes.data : [];
                const safeFavoriteCompanies = Array.isArray(currentUserRes?.data?.favoriteCompanies)
                    ? currentUserRes.data.favoriteCompanies.map((id) => String(id))
                    : [];
                const safeCities = Array.isArray(citiesRes.data) ? citiesRes.data : [];

                setCompanies(refreshSortRef.current ? [...safeCompanies].sort(refreshSortRef.current) : safeCompanies);
                setIndustriesData(safeIndustries);
                setSavedFilterSets(safeFilterSets);
                setFavorites(new Set(safeFavoriteCompanies));
                setActiveCityNames(new Set(safeCities.map((c) => c.cityName)));
            } catch (error) {
                if (!isCurrent()) return;
                console.error('Error fetching discovery companies / industries / filter sets / favorites:', error);
            }
        };

        fetchCompaniesIndustriesAndFilterSets();
    }, []);

    // Resolves an industry display label (from a URL ?industry= param or directly from
    // an AI chip's structuredFilters.industry) to what selectedIndustry/aiIndustryGroup
    // actually filter on: a known AI_INDUSTRY_GROUPS key stays a group match; anything
    // else is fuzzy-matched against the NACE list to a single code. No match -> no-op,
    // rather than setting a filter that silently matches nothing.
    const resolveIndustryLabel = (label) => {
        const trimmedLabel = String(label || '').trim();
        if (!trimmedLabel) return;

        const normalizedLabel = trimmedLabel.toLowerCase();

        if (AI_INDUSTRY_GROUPS[normalizedLabel]) {
            setAiIndustryGroup(trimmedLabel);
            return;
        }

        if (industriesData.length === 0) return;

        const matchedIndustry = industriesData.find((industry) => {
            const normalizedName = String(industry.name || '').trim().toLowerCase();
            return (
                normalizedName === normalizedLabel ||
                normalizedName.includes(normalizedLabel) ||
                normalizedLabel.includes(normalizedName)
            );
        });

        if (matchedIndustry) {
            setSelectedIndustry(String(matchedIndustry.code));
        }
    };

    // One-shot: resolve an ?industry= query param (an AI-suggested display label,
    // e.g. "Financial Services") the same way. Only runs once industriesData has
    // loaded, and only ever applies once.
    const appliedIndustryParamRef = useRef(false);

    useEffect(() => {
        if (appliedIndustryParamRef.current) return;
        if (industriesData.length === 0) return;

        appliedIndustryParamRef.current = true;

        const industryParam = searchParams.get('industry');
        if (!industryParam) return;

        // Broad AI industry groups are already applied via the aiIndustryGroup
        // initializer above; only the single-code fuzzy match needs to run here.
        if (AI_INDUSTRY_GROUPS[industryParam.trim().toLowerCase()]) return;

        resolveIndustryLabel(industryParam);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [industriesData, searchParams]);

    const getCountryIso2ByName = (countryName) => {
        if (!countryName) return '';

        const normalizedName = String(countryName).trim().toLowerCase();
        const match = countriesData.find(
            (country) => String(country.name || '').trim().toLowerCase() === normalizedName
        );

        return match ? match.id : '';
    };

    // This page already supports every field the AI can suggest, so every chip
    // applies locally — no navigation needed (unlike Countries/Cities, which route
    // company-specific fields here).
    const handleChipClick = (sf) => {
        if (!sf) return;

        if (sf.country) {
            const iso2 = getCountryIso2ByName(sf.country);
            if (iso2) setSelectedCountry(iso2);
        }

        if (sf.city) setSelectedCity(sf.city);
        if (sf.industry) resolveIndustryLabel(sf.industry);

        if (sf.jobType === 'english-speaking' || sf.language === 'English') {
            setSelectedEnglishSpeaking(true);
        }

        if (sf.jobType === 'student') setSelectedStudentJobAreas(true);
        if (sf.jobType === 'internship') setSelectedInternship(true);
    };

    const sortCompaniesByName = () => {
        const newSortOrder = sortOrderName === 'asc' ? 'desc' : 'asc';

        refreshSortRef.current = (a, b) => {
            const nameA = a.title ? a.title.toLowerCase() : '';
            const nameB = b.title ? b.title.toLowerCase() : '';
            return newSortOrder === 'asc'
                ? nameA.localeCompare(nameB)
                : nameB.localeCompare(nameA);
        };
        const sortedCompanies = [...companies].sort(refreshSortRef.current);

        setCompanies(sortedCompanies);
        setSortOrderName(newSortOrder);
    };

    const sortCompaniesByCountry = () => {
        const newSortOrder = sortOrderCountry === 'asc' ? 'desc' : 'asc';

        refreshSortRef.current = (a, b) => {
            const countryA = a.country ? a.country.toLowerCase() : '';
            const countryB = b.country ? b.country.toLowerCase() : '';
            return newSortOrder === 'asc'
                ? countryA.localeCompare(countryB)
                : countryB.localeCompare(countryA);
        };
        const sortedCompanies = [...companies].sort(refreshSortRef.current);

        setCompanies(sortedCompanies);
        setSortOrderCountry(newSortOrder);
    };

    const sortCompaniesByCity = () => {
        const newSortOrder = sortOrderCity === 'asc' ? 'desc' : 'asc';

        refreshSortRef.current = (a, b) => {
            const cityA = a.cityRegion ? a.cityRegion.toLowerCase() : '';
            const cityB = b.cityRegion ? b.cityRegion.toLowerCase() : '';
            return newSortOrder === 'asc'
                ? cityA.localeCompare(cityB)
                : cityB.localeCompare(cityA);
        };
        const sortedCompanies = [...companies].sort(refreshSortRef.current);

        setCompanies(sortedCompanies);
        setSortOrderCity(newSortOrder);
    };

    const industryNameByCode = useMemo(() => {
        const map = new Map();

        (industriesData || []).forEach((industry) => {
            if (industry?.code) {
                map.set(String(industry.code), industry.name || String(industry.code));
            }
        });

        return map;
    }, [industriesData]);

    // aiIndustryGroup is already a human-readable label; a selectedIndustry
    // NACE code is resolved to its label inside the helper.
    const mobileAiSuggestions = useMemo(() => getDiscoveryAiSuggestions({
        discoveryType: PAGE_DISCOVERY_TYPE,
        selectedCountry,
        selectedCity,
        selectedIndustry: aiIndustryGroup || selectedIndustry,
        selectedStudyDirection,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        industryNameByCode,
    }), [
        selectedCountry,
        selectedCity,
        aiIndustryGroup,
        selectedIndustry,
        selectedStudyDirection,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        industryNameByCode,
    ]);

    // Opt this page into the shared mobile AI assistant (header trigger + bottom sheet).
    useMobileAiPageContext(
        {
            pageType: 'discovery-companies',
            mapFirst: true,
            mapScope: 'global',
            discoveryType: PAGE_DISCOVERY_TYPE,
            suggestions: mobileAiSuggestions,
        },
        { onApplyStructuredFilters: handleChipClick }
    );

    const sortCompaniesByIndustry = () => {
        const newSortOrder = sortOrderIndustry === 'asc' ? 'desc' : 'asc';

        const getPrimaryIndustryLabel = (company) => {
            const firstIndustryCode = company?.industries?.[0]?.industry;

            if (!firstIndustryCode) return '';

            return industryNameByCode.get(String(firstIndustryCode)) || String(firstIndustryCode);
        };

        refreshSortRef.current = (a, b) => {
            const industryA = getPrimaryIndustryLabel(a).toLowerCase();
            const industryB = getPrimaryIndustryLabel(b).toLowerCase();

            return newSortOrder === 'asc'
                ? industryA.localeCompare(industryB)
                : industryB.localeCompare(industryA);
        };
        const sortedCompanies = [...companies].sort(refreshSortRef.current);

        setCompanies(sortedCompanies);
        setSortOrderIndustry(newSortOrder);
    };

    const sortCompaniesByLastUpdate = () => {
        const newSortOrder = sortOrderLastUpdate === 'asc' ? 'desc' : 'asc';

        refreshSortRef.current = (a, b) => {
            const dateA = new Date(a.latestDataUpdate || a.updatedAt || 0).getTime();
            const dateB = new Date(b.latestDataUpdate || b.updatedAt || 0).getTime();

            return newSortOrder === 'asc'
                ? dateA - dateB
                : dateB - dateA;
        };
        const sortedCompanies = [...companies].sort(refreshSortRef.current);

        setCompanies(sortedCompanies);
        setSortOrderLastUpdate(newSortOrder);
    };

    const sortCompaniesByGlobalSize = () => {
        const newSortOrder = sortOrderGlobalSize === 'asc' ? 'desc' : 'asc';

        const getCompanySizeSortValue = (value) => {
            const raw = String(value || '').trim().toLowerCase();

            if (!raw || raw === 'n/a' || raw === 'na' || raw === '-') return 0;
            if (raw.includes('several 1000')) return 3000;
            if (raw.includes('under 30')) return 30;
            if (raw.includes('over 100000')) return 100001;

            const rangeMatch = raw.match(/(\d[\d,.\s]*)\s*[-–—]\s*(\d[\d,.\s]*)/);
            if (rangeMatch) {
                const min = Number(rangeMatch[1].replace(/[^\d]/g, ''));
                const max = Number(rangeMatch[2].replace(/[^\d]/g, ''));
                if (!Number.isNaN(min) && !Number.isNaN(max)) {
                    return (min + max) / 2;
                }
            }

            const numericValue = Number(raw.replace(/[^\d]/g, ''));
            return Number.isNaN(numericValue) ? 0 : numericValue;
        };

        refreshSortRef.current = (a, b) => {
            const sizeA = getCompanySizeSortValue(a.globalSize || a.localSize);
            const sizeB = getCompanySizeSortValue(b.globalSize || b.localSize);

            return newSortOrder === 'asc'
                ? sizeA - sizeB
                : sizeB - sizeA;
        };
        const sortedCompanies = [...companies].sort(refreshSortRef.current);

        setCompanies(sortedCompanies);
        setSortOrderGlobalSize(newSortOrder);
    };

    const sortCompaniesByRating = () => {
        const newSortOrder = sortOrderRating === 'asc' ? 'desc' : 'asc';

        refreshSortRef.current = (a, b) => {
            const ratingA = Number(a.googleRating) || 0;
            const ratingB = Number(b.googleRating) || 0;

            return newSortOrder === 'asc'
                ? ratingA - ratingB
                : ratingB - ratingA;
        };
        const sortedCompanies = [...companies].sort(refreshSortRef.current);

        setCompanies(sortedCompanies);
        setSortOrderRating(newSortOrder);
    };

    const totalPages = Math.ceil(filteredCompanies.length / companiesPerPage);
    const noCompanies = filteredCompanies.length === 0;

    const companiesWithCoordinates = filteredCompanies.filter((company) => {
        const lat = Number(company.latitude);
        const lng = Number(company.longitude);

        if (Number.isNaN(lat) || Number.isNaN(lng)) return false;
        if (company.cityRegion && !activeCityNames.has(company.cityRegion)) return false;

        return true;
    });

    const anyCompaniesWithCoordinates = companies.some((company) => {
        const lat = Number(company.latitude);
        const lng = Number(company.longitude);

        return !Number.isNaN(lat) && !Number.isNaN(lng);
    });

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    const getPaginationItems = () => {
        const siblingCount = 2;
        const boundaryCount = 1;

        if (totalPages <= 1) return [1];

        const range = (start, end) => {
            const out = [];
            for (let i = start; i <= end; i++) out.push(i);
            return out;
        };

        const startPages = range(1, Math.min(boundaryCount, totalPages));
        const endPages = range(
            Math.max(totalPages - boundaryCount + 1, boundaryCount + 1),
            totalPages
        );

        const siblingsStart = Math.max(
            Math.min(
                currentPage - siblingCount,
                totalPages - boundaryCount - siblingCount * 2 - 1
            ),
            boundaryCount + 2
        );

        const siblingsEnd = Math.min(
            Math.max(
                currentPage + siblingCount,
                boundaryCount + siblingCount * 2 + 2
            ),
            totalPages - boundaryCount - 1
        );

        const items = [
            ...startPages,
            ...(siblingsStart > boundaryCount + 2
                ? ['ellipsis']
                : boundaryCount + 1 < totalPages - boundaryCount
                    ? [boundaryCount + 1]
                    : []),
            ...range(siblingsStart, siblingsEnd),
            ...(siblingsEnd < totalPages - boundaryCount - 1
                ? ['ellipsis']
                : totalPages - boundaryCount > boundaryCount
                    ? [totalPages - boundaryCount]
                    : []),
            ...endPages,
        ];

        return items.filter((v, i) => items.indexOf(v) === i);
    };

    const discoveryNavItems = [
        { to: '/discovery/countries', eventKey: 'countries', label: 'Countries' },
        { to: '/discovery/cities', eventKey: 'cities', label: 'Cities' },
        { to: '/discovery/companies/jobs', eventKey: 'companies', label: 'Companies & Jobs', alsoActive: ['/discovery/companies', '/discovery/jobs'] },
    ];

    const toggleFavorite = async (companyId) => {
        const companyIdStr = String(companyId);
        const previousFavorites = new Set(favorites);
        const wasFavorited = previousFavorites.has(companyIdStr);

        const optimisticFavorites = new Set(previousFavorites);

        if (wasFavorited) {
            optimisticFavorites.delete(companyIdStr);
        } else {
            optimisticFavorites.add(companyIdStr);
        }

        setFavorites(optimisticFavorites);

        try {
            const res = await api.patch(`/api/users/me/favorite-companies/${companyIdStr}`);

            const safeFavoriteCompanies = Array.isArray(res?.data?.favoriteCompanies)
                ? res.data.favoriteCompanies.map((id) => String(id))
                : [];

            setFavorites(new Set(safeFavoriteCompanies));

            const company = companies.find((c) => String(c._id) === companyIdStr);
            trackEvent(
                wasFavorited ? ANALYTICS_EVENTS.FAVORITE_COMPANY_REMOVED : ANALYTICS_EVENTS.FAVORITE_COMPANY_ADDED,
                {
                    companyId: companyIdStr,
                    companyName: company?.title || '',
                    level: 1,
                    source: 'companies_list',
                }
            );
        } catch (error) {
            console.error('Error toggling favorite company:', error);
            setFavorites(previousFavorites);
        }
    };

    const captureCurrentMobileFilters = () => captureDiscoveryFilters({
        searchQuery,
        selectedCountry,
        selectedCity,
        selectedIndustry,
        aiIndustryGroup,
        selectedStudyDirection,
        selectedCompanySize,
        selectedCompanyRating,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        selectedInternship,
        selectedFavorites,
    });

    const restoreMobileFilters = (filters) => {
        setSelectedCountry(filters.selectedCountry);
        setSelectedCity(filters.selectedCity);
        setSelectedIndustry(filters.selectedIndustry);
        setAiIndustryGroup(filters.aiIndustryGroup);
        setSelectedStudyDirection(filters.selectedStudyDirection);
        setSelectedCompanySize(filters.selectedCompanySize);
        setSelectedCompanyRating(filters.selectedCompanyRating);
        setSelectedEnglishSpeaking(filters.selectedEnglishSpeaking);
        setSelectedWorldTraveler(filters.selectedWorldTraveler);
        setSelectedStudentJobAreas(filters.selectedStudentJobAreas);
        setSelectedInternship(filters.selectedInternship);
        setSelectedFavorites(filters.selectedFavorites);
    };

    const closeMobileFilters = () => {
        restoreMobileFilters(appliedMobileFilters);
        setShowMobileFilters(false);
    };

    const applyMobileFilters = () => {
        setAppliedMobileFilters(captureCurrentMobileFilters());
        setShowMobileFilters(false);
    };

    const clearMobileFiltersPreserveSearch = () => {
        const clearedFilters = captureDiscoveryFilters({});
        restoreMobileFilters(clearedFilters);
        setAppliedMobileFilters(clearedFilters);
    };

    const clearEmptyStateSearchAndFilters = () => {
        clearMobileFiltersPreserveSearch();
        setSearchQuery('');
    };

    const removeAppliedMobileFilter = (key) => {
        const clearedValue = key.startsWith('selected') && [
            'selectedEnglishSpeaking',
            'selectedWorldTraveler',
            'selectedStudentJobAreas',
            'selectedInternship',
            'selectedFavorites',
        ].includes(key) ? null : (key === 'aiIndustryGroup' ? null : '');
        const nextFilters = { ...appliedMobileFilters, [key]: clearedValue };
        restoreMobileFilters(nextFilters);
        setAppliedMobileFilters(nextFilters);
    };

    const mobileSortOptions = mobileCompanySortOptions;
    const activeMobileSortOption = mobileSortOptions.find((option) => option.field === sortField);

    const clearMobileSort = () => {
        setSortField(null);
        setSortDirection(null);
    };

    const openMobileSort = () => {
        setDraftSortField(sortField);
        setDraftSortDirection(sortDirection);
        setShowMobileSort(true);
    };

    const closeMobileSort = () => {
        setDraftSortField(sortField);
        setDraftSortDirection(sortDirection);
        setShowMobileSort(false);
    };

    const clearMobileSortFromDrawer = () => {
        clearMobileSort();
        setDraftSortField(null);
        setDraftSortDirection(null);
        setShowMobileSort(false);
    };

    const selectDraftSort = (field) => {
        setDraftSortDirection((currentDirection) => (
            draftSortField === field
                ? (currentDirection === 'asc' ? 'desc' : 'asc')
                : mobileCompanySortDefinitions[field].defaultDirection
        ));
        setDraftSortField(field);
    };

    const applyMobileSort = () => {
        setSortField(draftSortField);
        setSortDirection(draftSortDirection);
        setShowMobileSort(false);
    };

    const appliedMobileChips = buildDiscoveryFilterChipLabels(appliedMobileFilters, industryNameByCode)
        .map((chip) => ({
            ...chip,
            onRemove: () => removeAppliedMobileFilter(chip.key),
        }));

    if (activeMobileSortOption) {
        appliedMobileChips.push({
            key: 'activeSort',
            label: activeMobileSortOption.label,
            icon: sortDirection === 'asc'
                ? <FaCaretUp aria-hidden="true" />
                : <FaCaretDown aria-hidden="true" />,
            onOpen: openMobileSort,
            onRemove: clearMobileSort,
            removeLabel: `Clear ${activeMobileSortOption.label} sort`,
        });
    }

    return (
        <>
            <Sidebar />
            <main className={`main-content flex-grow-1 discovery-companies-page discovery-map-page${isMobileNavDocked ? ' is-mobile-nav-docked' : ''}`}>
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>

                    <Row className="mb-4">
                        <Col>
                            <Card className="discovery-shell-card">
                                <Card.Body>
                                    <Row className="discovery-page-title-row">
                                        <Col xs={12}>
                                            <h1>Discover</h1>
                                        </Col>
                                    </Row>

                                    {anyCompaniesWithCoordinates && (
                                        <Row ref={mobileMapRowRef} className="discovery-map-row">
                                            <Col xs={12} className="px-0">
                                                <div className="discovery-mobile-map-area">
                                                    <DiscoveryMap
                                                        items={companiesWithCoordinates}
                                                        height={260}
                                                        initialCenter={mapInitialCenter}
                                                        flyToCenter={mapFlyTo}
                                                        fitBoundsPadding={60}
                                                        markerScale={0.6}
                                                        clusterPixelRadius={8}
                                                        maxClusterZoom={9}
                                                        groupClickZoom={11}
                                                        canCluster={(a, b) => a.country === b.country}
                                                        getLatitude={(company) => company.latitude}
                                                        getLongitude={(company) => company.longitude}
                                                        getTitle={(company) => company.title}
                                                        getLogo={(company) => company.logo || defaultCompanyMarkerLogo}
                                                        getLink={(company) => `/discovery/companies/${company._id}`}
                                                        onLinkClick={(company) => trackEvent(ANALYTICS_EVENTS.COMPANY_OPENED, {
                                                            companyId: company._id,
                                                            companyName: company.title,
                                                            source: 'companies_map',
                                                            level: 1,
                                                            viewMode: 'companies',
                                                        })}
                                                    />
                                                </div>
                                            </Col>
                                        </Row>
                                    )}
                                    <Row className="mb-4 discovery-tabs-row">
                                        <Col>
                                            <NavTabs
                                                items={discoveryNavItems}
                                                className="discovery-route-tabs"
                                                autoRevealActive
                                                trackOverflow
                                            />
                                        </Col>
                                    </Row>

                                    <DiscoveryDesktopAiSlot />

                                    {!isMobile && (
                                        <Row className="mb-3">
                                            <Col xs={12}>
                                                <DiscoveryFilterBar
                                                    title="Companies"
                                                    cityField="cityRegion"
                                                    allowedCities={activeCityNames}
                                                    viewOptions={[
                                                        { value: 'companies', label: 'Companies' },
                                                        { value: 'jobs', label: 'Jobs' },
                                                    ]}
                                                    activeView="companies"
                                                    onViewChange={(view) => {
                                                        if (view === 'jobs') {
                                                            trackEvent(ANALYTICS_EVENTS.DISCOVERY_VIEW_SWITCHED, {
                                                                discoveryType: 'companies',
                                                                fromViewMode: 'companies',
                                                                toViewMode: 'jobs',
                                                                level: 1,
                                                                source: 'companies_jobs_toggle',
                                                            });

                                                            navigate('/discovery/jobs');
                                                        }
                                                    }}
                                                    items={companies}
                                                    industryNameByCode={industryNameByCode}
                                                    searchQuery={searchQuery}
                                                    setSearchQuery={setSearchQuery}
                                                    selectedCountry={selectedCountry}
                                                    setSelectedCountry={setSelectedCountry}
                                                    selectedCity={selectedCity}
                                                    setSelectedCity={setSelectedCity}
                                                    selectedIndustry={selectedIndustry}
                                                    setSelectedIndustry={setSelectedIndustry}
                                                    selectedStudyDirection={selectedStudyDirection}
                                                    setSelectedStudyDirection={setSelectedStudyDirection}
                                                    selectedCompanySize={selectedCompanySize}
                                                    setSelectedCompanySize={setSelectedCompanySize}
                                                    selectedCompanyRating={selectedCompanyRating}
                                                    setSelectedCompanyRating={setSelectedCompanyRating}
                                                    selectedEnglishSpeaking={selectedEnglishSpeaking}
                                                    setSelectedEnglishSpeaking={setSelectedEnglishSpeaking}
                                                    selectedWorldTraveler={selectedWorldTraveler}
                                                    setSelectedWorldTraveler={setSelectedWorldTraveler}
                                                    selectedStudentJobAreas={selectedStudentJobAreas}
                                                    setSelectedStudentJobAreas={setSelectedStudentJobAreas}
                                                    aiIndustryGroupLabel={aiIndustryGroup}
                                                    onClearAiIndustryGroup={clearAiIndustryGroup}
                                                    selectedFavorites={selectedFavorites}
                                                    setSelectedFavorites={setSelectedFavorites}
                                                    savedFilterSets={savedFilterSets}
                                                    selectedFilterSet={selectedFilterSet}
                                                    onFilterSetChange={handleFilterSetChange}
                                                    onDeleteFilterSet={handleDeleteFilterSet}
                                                    onSaveFilters={handleSaveFilters}
                                                />
                                            </Col>
                                        </Row>
                                    )}

                                    {isMobile && (
                                        <>
                                            <MobileDiscoveryViewSwitch activeView="companies" />

                                            <MobileDiscoveryHeading title="Companies" />

                                            <MobileDiscoverySearchToolbar
                                                searchValue={searchQuery}
                                                onSearchChange={setSearchQuery}
                                                searchPlaceholder="Search..."
                                                searchAriaLabel="Search companies"
                                                actions={[
                                                    {
                                                        label: 'Open filters',
                                                        icon: icFilter,
                                                        onClick: () => setShowMobileFilters(true),
                                                    },
                                                    {
                                                        label: 'Sort companies',
                                                        icon: icSort,
                                                        onClick: openMobileSort,
                                                    },
                                                ]}
                                            />

                                            <MobileAppliedChips chips={appliedMobileChips} />

                                            <DiscoveryFilterBar
                                                title="Companies"
                                                cityField="cityRegion"
                                                allowedCities={activeCityNames}
                                                viewOptions={[
                                                    { value: 'companies', label: 'Companies' },
                                                    { value: 'jobs', label: 'Jobs' },
                                                ]}
                                                activeView="companies"
                                                onViewChange={(view) => {
                                                    if (view === 'jobs') {
                                                        trackEvent(ANALYTICS_EVENTS.DISCOVERY_VIEW_SWITCHED, {
                                                            discoveryType: 'companies',
                                                            fromViewMode: 'companies',
                                                            toViewMode: 'jobs',
                                                            level: 1,
                                                            source: 'companies_jobs_toggle',
                                                        });

                                                        navigate('/discovery/jobs');
                                                    }
                                                }}
                                                items={companies}
                                                industryNameByCode={industryNameByCode}
                                                searchQuery={searchQuery}
                                                setSearchQuery={setSearchQuery}
                                                selectedCountry={selectedCountry}
                                                setSelectedCountry={setSelectedCountry}
                                                selectedCity={selectedCity}
                                                setSelectedCity={setSelectedCity}
                                                selectedIndustry={selectedIndustry}
                                                setSelectedIndustry={setSelectedIndustry}
                                                selectedStudyDirection={selectedStudyDirection}
                                                setSelectedStudyDirection={setSelectedStudyDirection}
                                                selectedCompanySize={selectedCompanySize}
                                                setSelectedCompanySize={setSelectedCompanySize}
                                                selectedCompanyRating={selectedCompanyRating}
                                                setSelectedCompanyRating={setSelectedCompanyRating}
                                                selectedEnglishSpeaking={selectedEnglishSpeaking}
                                                setSelectedEnglishSpeaking={setSelectedEnglishSpeaking}
                                                selectedWorldTraveler={selectedWorldTraveler}
                                                setSelectedWorldTraveler={setSelectedWorldTraveler}
                                                selectedStudentJobAreas={selectedStudentJobAreas}
                                                setSelectedStudentJobAreas={setSelectedStudentJobAreas}
                                                aiIndustryGroupLabel={aiIndustryGroup}
                                                onClearAiIndustryGroup={clearAiIndustryGroup}
                                                selectedFavorites={selectedFavorites}
                                                setSelectedFavorites={setSelectedFavorites}
                                                savedFilterSets={savedFilterSets}
                                                selectedFilterSet={selectedFilterSet}
                                                onFilterSetChange={handleFilterSetChange}
                                                onDeleteFilterSet={(filterSetId) => handleDeleteFilterSet(filterSetId, { preserveSearch: true })}
                                                onSaveFilters={handleSaveFilters}
                                                onClearFilters={clearMobileFiltersPreserveSearch}
                                                mobileModalOnly
                                                mobileModalOpen={showMobileFilters}
                                                onMobileModalClose={closeMobileFilters}
                                                onMobileModalApply={applyMobileFilters}
                                                mobileModalTitle="Filter companies"
                                                mobileApplyLabel="Apply filters"
                                                hideTypeToggleOnMobile
                                                hideActiveFilterSummaryOnMobile
                                                useFullScreenMobilePanel
                                                mobileSavedFilterLabel="Saved filter"
                                                mobileSaveFilterLabel="Save current filters"
                                                compactSavedFilterOnMobile
                                                hideSearchInMobileModal
                                            />
                                        </>
                                    )}

                                    {isMobile ? (
                                        <MobileCompanyResults
                                            mobilePagination={mobilePagination}
                                            favorites={favorites}
                                            onFavoriteToggle={toggleFavorite}
                                            onClearFilters={clearEmptyStateSearchAndFilters}
                                            onOpenFilters={() => setShowMobileFilters(true)}
                                            onOpenCompany={(selectedCompany) => {
                                                mobilePagination.rememberScrollPosition();
                                                trackEvent(ANALYTICS_EVENTS.COMPANY_OPENED, {
                                                    companyId: selectedCompany._id,
                                                    companyName: selectedCompany.title,
                                                    source: 'companies_list',
                                                    level: 1,
                                                    viewMode: 'companies',
                                                });
                                                navigate(`/discovery/companies/${selectedCompany._id}`);
                                            }}
                                        />
                                    ) : noCompanies ? (
                                        <Row>
                                            <Col md={12}>
                                                <p className="mb-0">No companies available.</p>
                                            </Col>
                                        </Row>
                                    ) : (
                                        <Row>
                                            <Col>
                                                <div className="table-responsive list">
                                                    <Table className="discovery-table">
                                                        <thead>
                                                            <tr>
                                                                <th className="" onClick={sortCompaniesByName} style={{ cursor: 'pointer'}}>
                                                                    <div>
                                                                        Company
                                                                        <span>
                                                                            <FaCaretUp />
                                                                            <FaCaretDown />
                                                                        </span>
                                                                    </div>
                                                                </th>
                                                                <th className="" onClick={sortCompaniesByCountry} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Country
                                                                        <span>
                                                                            <FaCaretUp />
                                                                            <FaCaretDown />
                                                                        </span>
                                                                    </div>
                                                                </th>
                                                                <th className="" onClick={sortCompaniesByCity} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        City
                                                                        <span>
                                                                            <FaCaretUp />
                                                                            <FaCaretDown />
                                                                        </span>
                                                                    </div>
                                                                </th>
                                                                <th className="" onClick={sortCompaniesByIndustry} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Industry
                                                                        <span>
                                                                            <FaCaretUp />
                                                                            <FaCaretDown />
                                                                        </span>
                                                                    </div>
                                                                </th>
                                                                <th className="" onClick={sortCompaniesByGlobalSize} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Global Size
                                                                        <span>
                                                                            <FaCaretUp />
                                                                            <FaCaretDown />
                                                                        </span>
                                                                    </div>
                                                                </th>
                                                                <th className="" onClick={sortCompaniesByRating} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Rating
                                                                        <span>
                                                                            <FaCaretUp />
                                                                            <FaCaretDown />
                                                                        </span>
                                                                    </div>
                                                                </th>
                                                                <th className="">
                                                                    <div>
                                                                        Website
                                                                    </div>
                                                                </th>
                                                                <th className="" onClick={sortCompaniesByLastUpdate} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Last Update
                                                                        <span>
                                                                            <FaCaretUp />
                                                                            <FaCaretDown />
                                                                        </span>
                                                                    </div>
                                                                </th>
                                                                <th>Fav</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {currentCompanies.map((company) => (
                                                                <tr
                                                                    className="company-row"
                                                                    key={company._id}
                                                                    style={{ cursor: 'pointer' }}
                                                                    onClick={() => {
                                                                        trackEvent(ANALYTICS_EVENTS.COMPANY_OPENED, {
                                                                            companyId: company._id,
                                                                            companyName: company.title,
                                                                            source: 'companies_list',
                                                                            level: 1,
                                                                            viewMode: 'companies',
                                                                        });
                                                                        navigate(`/discovery/companies/${company._id}`);
                                                                    }}
                                                                >
                                                                    <td>
                                                                        <div>
                                                                            <img
                                                                                src={company.logo ? company.logo : defaultLogo}
                                                                                alt={company.title}
                                                                                className="round"
                                                                            />
                                                                            <span className="text-ellipsis text-ellipsis-country-name">
                                                                                <strong>{company.title}</strong>
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                    <td>{company.country || 'n/a'}</td>
                                                                    <td>{company.cityRegion || 'n/a'}</td>
                                                                    <td>
                                                                        {Array.isArray(company.industries) && company.industries.length > 0
                                                                            ? (industryNameByCode.get(String(company.industries[0]?.industry)) ||
                                                                                company.industries[0]?.industry ||
                                                                                'n/a')
                                                                            : 'n/a'}
                                                                    </td>
                                                                    <td>
                                                                        {company.globalSize || 'n/a'}
                                                                    </td>
                                                                    <td>
                                                                        {company.googleRating > 0 ? (
                                                                            <span className="d-inline-flex align-items-center gap-1">
                                                                                {company.googleRating.toFixed(1)}
                                                                                <img src={icStar} alt="rating" style={{ width: '14px', height: '14px' }} />
                                                                            </span>
                                                                        ) : (
                                                                            '—'
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        {company.url ? (
                                                                            <a
                                                                                href={company.url.startsWith('http') ? company.url : `https://${company.url}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="d-inline-flex align-items-center"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    trackEvent(ANALYTICS_EVENTS.COMPANY_WEBSITE_CLICKED, {
                                                                                        companyId: company._id,
                                                                                        companyName: company.title,
                                                                                        urlType: 'website',
                                                                                        level: 1,
                                                                                        source: 'companies_list',
                                                                                    });
                                                                                }}
                                                                            >
                                                                                <img src={icLink} alt="website" style={{ width: '16px', height: '16px' }} />
                                                                            </a>
                                                                        ) : (
                                                                            '—'
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        {company.latestDataUpdate || company.updatedAt ? (
                                                                            new Date(company.latestDataUpdate || company.updatedAt).toLocaleDateString()
                                                                        ) : (
                                                                            '—'
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        <span
                                                                            className="cursor-pointer d-inline-flex align-items-center"
                                                                            style={{ cursor: 'pointer' }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                toggleFavorite(company._id);
                                                                            }}
                                                                        >
                                                                            <img
                                                                                src={favorites.has(String(company._id)) ? icHeartFull : icHeartEmpty}
                                                                                alt="favorite"
                                                                                style={{ width: '16px', height: '16px' }}
                                                                            />
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}

                                    {!isMobile && <Row className="pagination-wrapper">
                                        <Col className="d-flex align-items-center">
                                            <span>
                                                Total {filteredCompanies.length} {filteredCompanies.length === 1 ? 'company' : 'companies'}
                                            </span>
                                            <Pagination>
                                                <Pagination.Prev
                                                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                                    disabled={currentPage === 1}
                                                >
                                                    <BsChevronLeft />
                                                </Pagination.Prev>

                                                {getPaginationItems().map((item, index) =>
                                                    item === 'ellipsis' ? (
                                                        <Pagination.Ellipsis key={`e-${index}`} disabled />
                                                    ) : (
                                                        <Pagination.Item
                                                            key={item}
                                                            active={item === currentPage}
                                                            onClick={() => handlePageChange(item)}
                                                        >
                                                            {item}
                                                        </Pagination.Item>
                                                    )
                                                )}

                                                <Pagination.Next
                                                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                                    disabled={currentPage === totalPages || totalPages === 0}
                                                >
                                                    <BsChevronRight />
                                                </Pagination.Next>
                                            </Pagination>
                                        </Col>

                                        <Col className="pagination-controls">
                                            <div className="pagination-controls-per-page">
                                                <Form.Select
                                                    value={companiesPerPage}
                                                    onChange={(e) => {
                                                        setCompaniesPerPage(Number(e.target.value));
                                                        setCurrentPage(1);
                                                    }}
                                                >
                                                    <option value="10">10 / page</option>
                                                    <option value="20">20 / page</option>
                                                    <option value="40">40 / page</option>
                                                </Form.Select>
                                            </div>

                                            <label className="pagination-controls-goto-label" htmlFor="goto-page">
                                                Go to
                                            </label>

                                            <Form.Control
                                                className="pagination-controls-goto-input"
                                                type="number"
                                                value={currentPage}
                                                onChange={(e) => {
                                                    const newPage = Number(e.target.value);
                                                    if (newPage >= 1 && newPage <= totalPages) {
                                                        setCurrentPage(newPage);
                                                    }
                                                }}
                                                onBlur={() => {
                                                    if (currentPage < 1) setCurrentPage(1);
                                                    if (currentPage > totalPages) setCurrentPage(totalPages);
                                                }}
                                            />
                                        </Col>
                                    </Row>}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </main>

            <MobileCompaniesSortDrawer
                showMobileSort={showMobileSort}
                closeMobileSort={closeMobileSort}
                mobileSortOptions={mobileSortOptions}
                draftSortField={draftSortField}
                draftSortDirection={draftSortDirection}
                selectDraftSort={selectDraftSort}
                clearMobileSortFromDrawer={clearMobileSortFromDrawer}
                applyMobileSort={applyMobileSort}
            />
        </>
    );
};

export default DiscoveryCompanyList;
